import React, { useEffect, useReducer } from 'react'
import { Button } from '@/Components/ui/button'
import Datatable from '@/Components/Common/Datatable'
import { Input } from '@/Components/ui/input'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import Constant from '@/utils/Constant'
import HeaderCard from '@/Components/HeaderCard'
import BodyCard from '@/Components/BodyCard'
import { Plus, SquarePlus, Trash2 } from 'lucide-react'
import DataTableEnumType from '@/Enums/DataTableTypeEnum'
import { useAuth } from '@/Context/AuthContext'
import RequiredPermission from '@/assets/data/RequiredPermission'
import { checkPermission, generateRandomeString } from '@/utils/helper'
import DeleteConfirmationTypeEnum from '@/Enums/DeleteConfirmationTypeEnum'
import ConfirmationPopup from '@/Components/Common/ConfirmationPopup'
import GroupConfigurationColumn from './GroupConfigurationColumns'
import GroupConfigurationService from '@/Service/GroupConfigurationService'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs'
import WorkAllocationTab from '@/assets/data/WorkAllocationTab'
import GroupConfigurationTabEnum from '@/Enums/GroupConfigurationTabEnum'
import { Checkbox } from '@/Components/ui/checkbox'
import SearchableDropdown from '@/Components/Common/SearchableDropdown'
import UserService from '@/Service/UserService'
import { Select } from '@radix-ui/react-select'
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select'
import GroupConfigurationRole from '@/assets/data/GroupConfigurationRole'
import TextLabel from '@/Components/Common/TextLabel'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/Components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover'
import GroupConfigurationSearchableDropdown from '@/Components/Common/GroupConfigurationSearchableDropdown'
import { toast } from 'react-toastify'
import { useSidebar } from '@/Components/ui/sidebar'
import EmployeeAllocations from '../EmployeeAllocations'
import AssignmentConfiguration from '../AssignmentConfiguration'

const GroupConfigurationListing = () => {
    const { userInfo } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const url = new URLSearchParams(location.search)
    const { open } = useSidebar()
    const [event, updateEvent] = useReducer(
        (prev, next) => {
            if (next?.groupAllEmployeeChecked !== undefined && Object.keys(prev.employeeConfigurationGroupWise).length) {
                let groups = Object.keys(next?.groupAllEmployeeChecked)
                if (groups.length) {
                    groups.forEach((group) => {
                        prev.employeeConfigurationGroupWise[group] = prev.employeeConfigurationGroupWise[group].map((employee) => {
                            employee['checkbox'] = next?.groupAllEmployeeChecked[group]
                            return employee
                        })
                    })
                }
            }
            return { ...prev, ...next }
        },
        {
            totalGroupConfigurationCount: 0,
            isDataFetching: false,
            deleteConfirmationOpen: false,
            groupConfigurations: [],
            allGroupConfigurations: [],
            employeeConfigurationGroupWise: {},
            groupAllEmployeeChecked: {},
            employee: [],
            currentRole: url?.get('role') && url?.get('role') != 'undefined' ? url?.get('role') : 'Medical Coder',
            currentTab: url?.get('tab') && url?.get('tab') != 'undefined' ? url?.get('tab') : GroupConfigurationTabEnum.GROUP_CONFIGURATION,
            groupSearch: '',
            isOpen: false,
            deleteId: [],
            allcheck: false,
            priorityChangeEvent: 0,
            queryParam: {
                page: 1,
                group_name: '',
            },
        }
    )
    const [debouncedGroupSearch] = useDebounce(event.groupSearch, Constant.DEBOUNCE_DELAY)

    const getGroupConfigurations = async () => {
        updateEvent({ isDataFetching: true, groupConfigurations: [] })
        try {
            const resp = await GroupConfigurationService.getGroupConfigurations({ ...event.queryParam, role: event.currentRole })
            if (resp.data.success) {
                updateEvent({
                    groupConfigurations: resp.data.data,
                    totalGroupConfigurationCount: resp.data.pagination.total_record,
                })
            }
        } catch (err) {}
        updateEvent({ isDataFetching: false })
    }

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const page = parseInt(searchParams.get('page') || 1)
        updateEvent({
            queryParam: {
                ...event.queryParam,
                page,
            },
        })
    }, [location.search])

    useEffect(() => {
        if (event.currentTab == GroupConfigurationTabEnum.GROUP_CONFIGURATION) {
            getGroupConfigurations()
        }
    }, [event.queryParam.page, event.queryParam.group_name, event.currentRole])

    useEffect(() => {
        url.set('tab', event.currentTab)
        if ([GroupConfigurationTabEnum.EMPLOYEE_ALLOCATION, GroupConfigurationTabEnum.GROUP_CONFIGURATION].includes(event.currentTab)) {
            url.set('role', event.currentRole)
        }
        navigate(`${location.pathname}?${url.toString()}`, { replace: true })
        updateEvent({ employeeConfigurationGroupWise: {}, groupAllEmployeeChecked: {} })
    }, [event.currentTab, event.currentRole])

    useEffect(() => {
        updateEvent({
            queryParam: {
                ...event.queryParam,
                group_name: debouncedGroupSearch,
            },
        })
    }, [debouncedGroupSearch])

    const deleteGroupConfiguration = async (action) => {
        if (!action) {
            updateEvent({ deleteConfirmationOpen: false })
            return false
        }
        try {
            const resp = await GroupConfigurationService.deleteGroupConfiguration({ group_configuration: event.deleteId.map((id) => ({ name: id })) })
            if (resp.data.success) {
                getGroupConfigurations()
            }
            updateEvent({ deleteId: [], allcheck: false })
        } catch (err) {}
        updateEvent({ deleteConfirmationOpen: false })
    }

    return (
        <div>
            <HeaderCard>
                <div className="flex justify-between items-center">
                    {checkPermission(RequiredPermission.PROCESS.CREATE, userInfo.roles) && (
                        <Button
                            className="bg-primary-btn hover:bg-primary-purpal"
                            onClick={() => event.currentRole ? navigate('/configuration/group-configuration/new?role=' + event.currentRole) :navigate('/configuration/group-configuration/new') }
                        >
                            <SquarePlus />
                            Add Group Configuration
                        </Button>
                    )}

                    {checkPermission(RequiredPermission.PROCESS.DELETE, userInfo.roles) && event.deleteId.length > 0 && (
                        <Button
                            variant="outline"
                            onClick={() => updateEvent({ deleteConfirmationOpen: true })}
                            className={`ml-[10px] ${event.deleteId.length > 0 ? 'block' : 'hidden'}`}
                        >
                            Delete
                        </Button>
                    )}
                </div>
            </HeaderCard>
            <ConfirmationPopup
                open={event.deleteConfirmationOpen}
                message="Are you sure you want to delete this permanently?"
                type={DeleteConfirmationTypeEnum.DELETE}
                setOpen={() => updateEvent({ deleteConfirmationOpen: !event.deleteConfirmationOpen })}
                handleAction={deleteGroupConfiguration}
            />
            <BodyCard>
                <div className="grid  gap-y-2 gap-x-2 h-full overflow-hidden">
                    <Tabs
                        defaultValue={event.currentTab}
                        className="relative mr-auto h-full"
                        style={{ width: open ? 'calc(100vw - 276px)' : 'calc(100vw + -50px)' }}
                    >
                        <TabsList className="flex  rounded-t-lg justify-start rounded-none border-b bg-transparent p-0 mb-0 ">
                            <TabsTrigger
                                className="inline-flex items-center justify-center whitespace-nowrap py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 data-[state=active]:font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-black data-[state=active]:text-foreground data-[state=active]:shadow-none font-normal"
                                value={GroupConfigurationTabEnum.GROUP_CONFIGURATION}
                                onClick={() => {
                                    updateEvent({ currentTab: GroupConfigurationTabEnum.GROUP_CONFIGURATION })
                                    getGroupConfigurations()
                                }}
                            >
                                Group Configuration
                            </TabsTrigger>
                            <TabsTrigger
                                value={GroupConfigurationTabEnum.EMPLOYEE_ALLOCATION}
                                onClick={() => updateEvent({ currentTab: GroupConfigurationTabEnum.EMPLOYEE_ALLOCATION })}
                                className="inline-flex items-center justify-center whitespace-nowrap py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 data-[state=active]:font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-black data-[state=active]:text-foreground data-[state=active]:shadow-none font-normal"
                            >
                                Employee Allocations
                            </TabsTrigger>
                            <TabsTrigger
                                value={GroupConfigurationTabEnum.ASSIGNMENT_CONFIGURATION}
                                onClick={() => updateEvent({ currentTab: GroupConfigurationTabEnum.ASSIGNMENT_CONFIGURATION })}
                                className="inline-flex items-center justify-center whitespace-nowrap py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 data-[state=active]:font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-black data-[state=active]:text-foreground data-[state=active]:shadow-none font-normal"
                            >
                                Assignment Configuration
                            </TabsTrigger>
                        </TabsList>

                        {[GroupConfigurationTabEnum.EMPLOYEE_ALLOCATION, GroupConfigurationTabEnum.GROUP_CONFIGURATION].includes(
                            event.currentTab
                        ) && (
                            <div className="flex absolute top-0 right-0 justify-center items-center gap-2">
                                <TextLabel text="Role" />
                                <Select
                                    value={event.currentRole}
                                    onValueChange={(v) => {
                                        updateEvent({ currentRole: v })
                                    }}
                                >
                                    <SelectTrigger className="w-32">
                                        <SelectValue placeholder="&nbsp;" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {GroupConfigurationRole.map((role) => (
                                            <SelectItem key={role.key} value={role.key}>
                                                {role.value}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <TabsContent value={GroupConfigurationTabEnum.GROUP_CONFIGURATION}>
                            <div className="w-full my-2 grid grid-cols-4 gap-3">
                                <Input
                                    type="text"
                                    value={event.groupSearch}
                                    onChange={(e) => updateEvent({ groupSearch: e.target.value })}
                                    placeholder="Group Name"
                                />
                            </div>
                            <Datatable
                                columns={GroupConfigurationColumn()}
                                data={event.groupConfigurations}
                                totalDataCount={event.totalGroupConfigurationCount}
                                allcheck={event.allcheck}
                                deleteId={event.deleteId}
                                updateEvent={updateEvent}
                                loading={event.isDataFetching}
                                type={DataTableEnumType.GROUP_CONFIGURATION}
                            />
                        </TabsContent>
                        <TabsContent className="h-[91%] max-h-[100%]" value={GroupConfigurationTabEnum.EMPLOYEE_ALLOCATION}>
                            <EmployeeAllocations currentRole={event.currentRole} />
                        </TabsContent>
                        <TabsContent value={GroupConfigurationTabEnum.ASSIGNMENT_CONFIGURATION} className="overflow-hidden">
                            <AssignmentConfiguration />
                        </TabsContent>
                    </Tabs>
                </div>
            </BodyCard>
        </div>
    )
}

export default GroupConfigurationListing
